import React from 'react';
import { motion } from 'framer-motion';
import { Wind, Brain, Anchor, ZapOff, Activity, Eye, Layout, BookOpen, Sparkles, Zap } from 'lucide-react';
import { Button } from './ui/Button';
const days = [{
  day: 'Day 1',
  title: 'The "Alarm-Off" Breathing Protocol',
  description: 'Lower physiological arousal in minutes. No meditation required. Most people feel relief within the first session.',
  icon: Wind,
  color: 'bg-indigo-100 text-indigo-700'
}, {
  day: 'Day 2',
  title: 'The Spiral Stopper',
  description: 'Break rumination loops before they build momentum. A fast method you can use anywhere — even at work.',
  icon: Brain,
  color: 'bg-violet-100 text-violet-700'
}, {
  day: 'Day 3',
  title: 'Stability Anchors',
  description: 'Tiny daily anchors that tell your nervous system: "Nothing is chasing me." This is where most people notice the shift.',
  icon: Anchor,
  color: 'bg-blue-100 text-blue-700'
}, {
  day: 'Day 4',
  title: 'Stimulation Diet',
  description: 'Identify and reduce the hidden inputs keeping your body wired. One change that multiplies your results.',
  icon: ZapOff,
  color: 'bg-rose-100 text-rose-700'
}, {
  day: 'Day 5',
  title: 'Tension Release',
  description: 'Release stored tension your body has been holding for months — without forcing "relaxation."',
  icon: Activity,
  color: 'bg-purple-100 text-purple-700'
}, {
  day: 'Day 6',
  title: 'External Focus Training',
  description: 'Exit the internal threat loop. A retraining drill that shifts attention out of body-scanning and into the present.',
  icon: Eye,
  color: 'bg-slate-100 text-slate-700'
}, {
  day: 'Day 7',
  title: 'Your Calm Blueprint',
  description: 'Build your personal "maintenance plan" so results don\'t disappear on Day 8. This is what makes it last.',
  icon: Layout,
  color: 'bg-emerald-100 text-emerald-700'
}];
export function WhatYouGetSection() {
  return <section id="the-plan" className="py-24 bg-[#F5F5FA] relative overflow-hidden" data-id="element-335">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-336">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="text-center mb-16" data-id="element-337">
          <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#4338CA] text-white text-sm font-bold mb-6 shadow-md" data-id="element-338">
            <BookOpen className="w-4 h-4" data-id="element-339" />
            THE 7-DAY PLAN
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-[#1E1B4B] mb-6 leading-tight" data-id="element-340">
            One{' '}
            <span className="relative inline-block" data-id="element-341">
              <span className="relative z-10" data-id="element-342">precise signal</span>
              <span className="absolute bottom-1 left-0 w-full h-3 bg-[#FDE68A]/60 -z-0 rounded-sm" data-id="element-343"></span>
            </span>{' '}
            per day.
            <br data-id="element-344" />
            <span className="text-[#4338CA]" data-id="element-345">Exit "threat mode."</span>
          </h2>
          <p className="text-xl text-[#44403C] max-w-2xl mx-auto leading-relaxed" data-id="element-346">
            Not motivation.{' '}
            <span className="font-bold text-[#1E1B4B]" data-id="element-347">Structure.</span> Each
            day targets a{' '}
            <span className="text-[#4338CA] font-semibold" data-id="element-348">
              different driver
            </span>{' '}
            of anxiety.
          </p>
        </motion.div>

        <div className="max-w-4xl mx-auto" data-id="element-349">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-350">
            <div className="space-y-6" data-id="element-351">
              {days.slice(0, 4).map((day, index) => <motion.div key={index} initial={{
              opacity: 0,
              x: -20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: index * 0.1
            }} className="bg-white p-6 rounded-xl border border-[#E0E7FF] shadow-sm hover:shadow-md transition-shadow" data-id="element-352">
                  <div className="flex items-start gap-4" data-id="element-353">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${day.color}`} data-id="element-354">
                      <day.icon className="w-5 h-5" data-id="element-355" />
                    </div>
                    <div data-id="element-356">
                      <span className="text-xs font-bold uppercase tracking-wider text-[#78716C] mb-1 block" data-id="element-357">
                        {day.day}
                      </span>
                      <h3 className="text-lg font-bold text-[#1E1B4B] mb-2" data-id="element-358">
                        {day.title}
                      </h3>
                      <p className="text-sm text-[#44403C] leading-relaxed" data-id="element-359">
                        {day.description}
                      </p>
                    </div>
                  </div>
                </motion.div>)}
            </div>

            <div className="space-y-6" data-id="element-360">
              {days.slice(4).map((day, index) => <motion.div key={index + 4} initial={{
              opacity: 0,
              x: 20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: (index + 4) * 0.1
            }} className="bg-white p-6 rounded-xl border border-[#E0E7FF] shadow-sm hover:shadow-md transition-shadow" data-id="element-361">
                  <div className="flex items-start gap-4" data-id="element-362">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${day.color}`} data-id="element-363">
                      <day.icon className="w-5 h-5" data-id="element-364" />
                    </div>
                    <div data-id="element-365">
                      <span className="text-xs font-bold uppercase tracking-wider text-[#78716C] mb-1 block" data-id="element-366">
                        {day.day}
                      </span>
                      <h3 className="text-lg font-bold text-[#1E1B4B] mb-2" data-id="element-367">
                        {day.title}
                      </h3>
                      <p className="text-sm text-[#44403C] leading-relaxed" data-id="element-368">
                        {day.description}
                      </p>
                    </div>
                  </div>
                </motion.div>)}

              
            </div>
          </div>
        </div>
      </div>
    </section>;
}